import React, { useEffect, useState } from 'react';
import styles from './CertificationsPage.module.css';

const MY_CERTIFICATIONS = [
  {
    title: "HTML, CSS, and Javascript for Web Developers",
    issuer: "Johns Hopkins University — Coursera",
    institutionShort: "Johns Hopkins University",
    year: "2026",
    category: "Web & Software Engineering",
    badgeText: "🌐 Full-Stack Front-End",
    badgeStyle: styles.badgeTeal,
    credentialId: "T8IN6LK4625L",
    details: "Full-stack web development fundamentals including responsive UI architecture, modern CSS3 layout systems, DOM manipulation, and JavaScript ES6+ patterns. Directly applied to architect and build this entire custom React.js academic portfolio website from scratch, as well as the interactive diagnostic dashboards in my research projects.",
    credentialUrl: "https://www.coursera.org/verify/T8IN6LK4625L",
    image: "/assets/certificates/HTML, CSS, and Javascript for Web Developers - coursera.png",
    skills: ["HTML5", "CSS3", "JavaScript ES6+", "Responsive UI Design", "DOM Architecture"]
  },
  {
    title: "Introduction and Programming with IoT Boards",
    issuer: "POSTECH (Pohang University of Science and Technology) — Coursera",
    institutionShort: "POSTECH",
    year: "2026",
    category: "Aerospace & Embedded Systems",
    badgeText: "🔌 Embedded & IoT",
    badgeStyle: styles.badgeBlue,
    credentialId: "L8LAB9OPGJA1",
    details: "Embedded microcontroller programming, smart sensor integration, wireless telemetry protocols, and Industry 4.0 automation workflows. Directly supports the Arduino-controlled closed-loop filament extrusion system in my rPET additive manufacturing research and UAV telemetry integration.",
    credentialUrl: "https://www.coursera.org/verify/L8LAB9OPGJA1",
    image: "/assets/certificates/Introduction to programming with IoT board - coursera.png",
    skills: ["IoT Systems", "Arduino", "Sensor Integration", "Industry 4.0", "Embedded C/C++"]
  },
  {
    title: "Introduction to Thermodynamics: Transferring Energy from Here to There",
    issuer: "University of Michigan — Coursera",
    institutionShort: "University of Michigan",
    year: "2023",
    category: "Aerospace & Embedded Systems",
    badgeText: "✈️ Propulsion & Thermal",
    badgeStyle: styles.badgePurple,
    credentialId: "KKWXUSP9EUQJ",
    details: "Rigorous study of thermodynamic cycles, conservation of energy, entropy analysis, and heat transfer mechanisms. Provides the physical domain foundation for gas turbine performance modeling and sensor degradation analysis in my turbofan engine predictive maintenance research.",
    credentialUrl: "https://coursera.org/verify/KKWXUSP9EUQJ",
    image: "/assets/certificates/thermodynamics-coursera.png",
    skills: ["Thermodynamics", "Brayton Cycle", "Heat Transfer", "Turbofan Propulsion", "Energy Systems"]
  },
  {
    title: "Excel Skills for Business: Intermediate I",
    issuer: "Macquarie University — Coursera",
    institutionShort: "Macquarie University",
    year: "2026",
    category: "Data & Business Analytics",
    badgeText: "📊 Data Analytics",
    badgeStyle: styles.badgeEmerald,
    credentialId: "S52ZEVYPO1H9",
    details: "Advanced multi-sheet data management, dynamic PivotTables, lookup functions, and conditional aggregation. Strengthens the analytical workflow for structuring multi-sensor aerospace telemetry datasets and ASTM D638 experimental test matrices.",
    credentialUrl: "https://coursera.org/verify/S52ZEVYPO1H9",
    image: "/assets/certificates/Excel Skills for Business Intermediate - coursera.png",
    skills: ["Advanced Excel", "PivotTables", "Data Aggregation", "Experimental Benchmarking", "Conditional Logic"]
  },
  {
    title: "Excel Skills for Business: Essentials",
    issuer: "Macquarie University — Coursera",
    institutionShort: "Macquarie University",
    year: "2026",
    category: "Data & Business Analytics",
    badgeText: "📈 Quantitative Modeling",
    badgeStyle: styles.badgeGold,
    credentialId: "LHUPTYEEK2B0",
    details: "Core quantitative spreadsheet engineering including analytical formulas, data validation, and scientific charting. Applied to engineering budget preparation (such as the BDT 55 Lakh World Bank RIC UAV grant proposal) and laboratory data logging.",
    credentialUrl: "https://coursera.org/verify/LHUPTYEEK2B0",
    image: "/assets/certificates/Excel Skills for Business Essentials - coursera.png",
    skills: ["Quantitative Modeling", "Data Visualization", "Grant Budgeting", "Formulas", "Data Validation"]
  }
];

export default function CertificationsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedCert, setSelectedCert] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!selectedCert) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedCert(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCert]);

  const categories = ['All', 'Aerospace & Embedded Systems', 'Web & Software Engineering', 'Data & Business Analytics'];

  const filteredCerts = activeCategory === 'All'
    ? MY_CERTIFICATIONS
    : MY_CERTIFICATIONS.filter((c) => c.category === activeCategory);

  return (
    <div className={styles.container}>
      {/* Page Header & Summary */}
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderTop}>
          <div>
            <span className={styles.pageEyebrow}>VERIFIED ACADEMIC & TECHNICAL CREDENTIALS</span>
            <h1 className={styles.pageTitle}>Certifications & Professional Training</h1>
            <p className={styles.pageSubtitle}>
              Verified coursework from globally recognized universities supporting my research in aerospace propulsion, embedded IoT systems, data analytics, and full-stack web engineering.
            </p>
          </div>
          <div className={styles.statsRow}>
            <div className={styles.statChip}>
              <span className={styles.statNum}>{MY_CERTIFICATIONS.length}</span>
              <span className={styles.statText}>Verified Certificates</span>
            </div>
            <div className={styles.statChip}>
              <span className={styles.statNum}>4</span>
              <span className={styles.statText}>Global Universities</span>
            </div>
            <div className={styles.statChip}>
              <span className={styles.statNum}>100%</span>
              <span className={styles.statText}>ID-Verified Links</span>
            </div>
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className={styles.filterBar} role="tablist" aria-label="Filter certifications by domain">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat}
              className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === 'All' ? `All Credentials (${MY_CERTIFICATIONS.length})` : cat}
            </button>
          ))}
        </div>
      </header>

      {/* Certificate Grid */}
      <div className={styles.grid}>
        {filteredCerts.map((cert, idx) => (
          <CertCard
            key={cert.credentialId || idx}
            cert={cert}
            onPreview={() => setSelectedCert(cert)}
          />
        ))}
      </div>

      {/* Custom Portfolio Attribution Banner */}
      <section className={styles.portfolioShowcaseCard} aria-label="Custom portfolio development">
        <div className={styles.showcaseContent}>
          <span className={styles.showcaseBadge}>💻 APPLIED WEB ENGINEERING IN ACTION</span>
          <h2 className={styles.showcaseTitle}>
            This Portfolio Website Was Entirely Developed by Myself
          </h2>
          <p className={styles.showcaseDesc}>
            Applying the front-end engineering principles from my <strong>Johns Hopkins University Web Development</strong> certification and hands-on React.js experience, I architected, designed, and coded this entire academic portfolio from scratch. If you are a researcher, academic, or professional who wants to build a similar custom portfolio website, feel free to contact me!
          </p>
        </div>
        <div className={styles.showcaseActions}>
          <a
            href="mailto:jolok21014026@aaub.edu.bd?subject=Inquiry%3A%20Building%20a%20Similar%20Portfolio%20Website"
            className={styles.showcaseBtnPrimary}
          >
            ✉️ Contact Me to Build Yours
          </a>
          <a
            href="https://www.linkedin.com/in/jolok-banarjee-16825b1b7/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.showcaseBtnSecondary}
          >
            Connect on LinkedIn ↗
          </a>
        </div>
      </section>

      {/* Certificate Lightbox Modal */}
      {selectedCert && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setSelectedCert(null)}
          role="dialog"
          aria-modal="true"
          aria-label={selectedCert.title}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>{selectedCert.title}</h3>
                <p className={styles.modalSub}>
                  {selectedCert.issuer} · Issued {selectedCert.year} · ID: {selectedCert.credentialId}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalCloseBtn}
                onClick={() => setSelectedCert(null)}
                aria-label="Close certificate preview"
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <img
                src={selectedCert.image}
                alt={`${selectedCert.title} Certificate`}
                className={styles.modalImage}
              />
              <p className={styles.modalCaption}>{selectedCert.details}</p>
              {selectedCert.credentialUrl && (
                <a
                  href={selectedCert.credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.verifyBtnPrimary}
                >
                  ✓ Verify Official Credential on Coursera <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CertCard({ cert, onPreview }) {
  return (
    <article className={styles.card}>
      {/* Certificate Image */}
      {cert.image && (
        <div
          className={styles.certImageWrapper}
          onClick={onPreview}
          title={`Click to enlarge ${cert.title} certificate`}
        >
          <img
            src={cert.image}
            alt={`${cert.title} Certificate`}
            className={styles.certImage}
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.style.display = 'none';
            }}
          />
          <span className={styles.zoomHint}>🔍 Click to Enlarge</span>
        </div>
      )}

      <div className={styles.cardBody}>
        <div className={styles.badgeRow}>
          <span className={`${styles.domainBadge} ${cert.badgeStyle || styles.badgeTeal}`}>
            {cert.badgeText || cert.category}
          </span>
          <span className={styles.yearPill}>{cert.year}</span>
        </div>

        <h3 className={styles.certTitle}>{cert.title}</h3>
        <p className={styles.certIssuer}>
          <span>🏛️ {cert.issuer}</span>
        </p>
        {cert.credentialId && (
          <p className={styles.credentialId}>
            Credential ID: <code>{cert.credentialId}</code>
          </p>
        )}

        <p className={styles.certDetails}>{cert.details}</p>

        <div className={styles.skillsRow}>
          {cert.skills.map((skill, i) => (
            <span key={i} className={styles.skillTag}>{skill}</span>
          ))}
        </div>

        <div className={styles.cardActions}>
          {cert.credentialUrl && (
            <a
              href={cert.credentialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.verifyBtnPrimary}
            >
              ✓ Verify Credential <span aria-hidden="true">↗</span>
            </a>
          )}
          <button
            type="button"
            onClick={onPreview}
            className={styles.previewBtnSecondary}
          >
            🔍 Preview
          </button>
        </div>
      </div>
    </article>
  );
}